import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AppBar from 'app/components/Layout/AppBar';
import Footer from 'app/components/Footer';
import {
  COVERAGE_FIELDS,
  Dataset,
  DatasetCategory,
  catalogueStats,
  datasets,
} from 'app/data/datasets';
import DatasetTable, { SortKey, SortState, sortDatasets } from './DatasetTable';
import DatasetRows from './DatasetRows';
import { fieldCoverage, hasDocumentedCoverage } from './coverage';

type CategoryFilter = DatasetCategory | 'all';
type ViewMode = 'table' | 'rows';

/** Name ascending is the scannable default for a catalogue index. */
const DEFAULT_SORT: SortState = { key: 'name', direction: 'asc' };

/** Magnitude columns read better starting descending — biggest first. */
const DESCENDING_FIRST: SortKey[] = ['entities'];

/**
 * A bordered panel with its label notched into the top border. The label sits
 * on the page ground colour so it appears to interrupt the hairline.
 */
function Panel({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`relative border border-gray-800 ${className}`}>
      <span className="absolute -top-[7px] left-3 bg-[#0d1117] px-1.5 font-mono text-[11px] uppercase tracking-wider text-gray-400">
        {label}
      </span>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="border-gray-800 px-3 py-2 sm:border-r sm:last:border-r-0">
      <div className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div className="font-mono text-lg tabular-nums leading-tight text-white">
        {value}
      </div>
      <div className="text-[11px] leading-tight text-gray-400">{detail}</div>
    </div>
  );
}

/**
 * Our own coverage, published as a dense mono strip — the "observed in
 * production" move, turned on ourselves. Reading `history 21/29 · cadence 2/29`
 * tells a buyer exactly where this catalogue is thin before they invest time in
 * it. It is deliberately not averaged into a single score: an average would let
 * a strong field paper over an empty one.
 */
function PublicationRateStrip({
  fields,
}: {
  fields: ReturnType<typeof fieldCoverage>;
}) {
  return (
    <Panel label="publication rate" className="px-3 pb-2 pt-3.5">
      <p className="sr-only">
        How many datasets publish each coverage field, out of the full
        catalogue.
      </p>
      <ul className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono text-[11px] leading-tight">
        {fields.map((field, index) => {
          const complete = field.documented === field.total;
          const empty = field.documented === 0;
          return (
            <li key={String(field.key)} className="flex items-baseline gap-1.5">
              <span
                className="text-gray-400"
                title={`${field.label} — ${field.why}`}
              >
                {field.short}
              </span>
              <span
                className={`tabular-nums ${
                  complete
                    ? 'text-green-400'
                    : empty
                    ? 'text-gray-400 line-through decoration-gray-600'
                    : 'text-white'
                }`}
              >
                {field.documented}/{field.total}
              </span>
              {index < fields.length - 1 && (
                <span aria-hidden="true" className="pl-1.5 text-gray-600">
                  ·
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

export function DatasetsPage() {
  const stats = useMemo(() => catalogueStats(), []);
  const coverageFields = useMemo(() => fieldCoverage(), []);

  const [category, setCategory] = useState<CategoryFilter>('all');
  const [documentedOnly, setDocumentedOnly] = useState(false);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [view, setView] = useState<ViewMode>('table');

  const handleSortChange = (key: SortKey) => {
    setSort(current =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: DESCENDING_FIRST.includes(key) ? 'desc' : 'asc' },
    );
  };

  /*
    No free-text search box. The whole catalogue is a few dozen rows and fits on
    a screen or two, so a search input would mostly be a slower way to do what
    the eye already does. The category chips and column sorts stay: they are
    one click and they answer questions scanning cannot.
  */
  const rows: Dataset[] = useMemo(() => {
    const filtered = datasets.filter(dataset => {
      if (category !== 'all' && dataset.category !== category) return false;
      if (documentedOnly && !hasDocumentedCoverage(dataset)) return false;
      return true;
    });
    return sortDatasets(filtered, sort);
  }, [category, documentedOnly, sort]);

  const filtersActive = category !== 'all' || documentedOnly;

  const clearFilters = () => {
    setCategory('all');
    setDocumentedOnly(false);
  };

  const categoryOptions: Array<{
    value: CategoryFilter;
    label: string;
    count: number;
  }> = [
    { value: 'all', label: 'all', count: stats.total },
    { value: 'equity', label: 'equity', count: stats.categories.equity },
    { value: 'economic', label: 'economic', count: stats.categories.economic },
    {
      value: 'sectorial',
      label: 'sectorial',
      count: stats.categories.sectorial,
    },
  ];

  const toggleClass = (active: boolean) =>
    `px-2.5 py-0.5 font-mono text-[11px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400 ${
      active ? 'bg-blue-500/15 text-blue-300' : 'text-gray-400 hover:text-white'
    }`;

  return (
    <div className="relative min-h-screen bg-[#0d1117]">
      <Helmet>
        <title>Dataset Catalogue - Sov.ai</title>
        <meta
          name="description"
          content={`Browse ${stats.total} Sov.ai alternative datasets across equity, economic and sectorial categories, with published history, entity coverage and universe for each.`}
        />
      </Helmet>

      <AppBar />

      <main className="mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 lg:px-8">
        <header className="mb-5">
          <h1 className="font-mono text-2xl font-semibold text-white md:text-3xl">
            Dataset Catalogue
          </h1>
          <p className="mt-2 max-w-3xl text-[13px] leading-relaxed text-gray-400">
            Every dataset we publish, with the coverage metadata we have
            verified. Fields we have not published are marked{' '}
            <span className="font-mono text-xs italic text-gray-400">
              Not yet documented
            </span>{' '}
            rather than estimated — on a catalogue, a wrong number is worse than
            a missing one.
          </p>
        </header>

        {/* Headline counts — all computed from the registry, never hardcoded. */}
        <section aria-label="Catalogue summary" className="mb-4">
          <h2 className="sr-only">Catalogue summary</h2>
          <Panel label="catalogue">
            <div className="grid grid-cols-2 divide-y divide-gray-800 sm:grid-cols-3 sm:divide-y-0 lg:grid-cols-5">
              <Stat
                label="datasets"
                value={String(stats.total)}
                detail={`${stats.categories.equity} equity · ${stats.categories.economic} economic · ${stats.categories.sectorial} sectorial`}
              />
              <Stat
                label="documented"
                value={`${stats.documented}/${stats.total}`}
                detail="published history start and entity count"
              />
              <Stat
                label="in beta"
                value={String(stats.beta)}
                detail="live, coverage figures not yet fixed"
              />
              <Stat
                label="earliest history"
                value={stats.earliestHistory ?? '—'}
                detail="deepest series in the catalogue"
              />
              <Stat
                label="largest cross-section"
                value={`${stats.deepestEntities.toLocaleString('en-US')}+`}
                detail="entities in the broadest dataset"
              />
            </div>
          </Panel>
        </section>

        {/* Our own coverage, stated before the table rather than after it. */}
        <section aria-label="Publication rate" className="mb-4">
          <h2 className="sr-only">Publication rate</h2>
          <PublicationRateStrip fields={coverageFields} />
        </section>

        {/* Filters */}
        <section aria-label="Filter datasets" className="mb-3">
          <h2 className="sr-only">Filter datasets</h2>
          <Panel label="filter" className="px-3 pb-2 pt-3.5">
            <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <div className="flex items-center gap-2">
                  <span
                    id="category-filter-label"
                    className="font-mono text-[11px] uppercase tracking-wider text-gray-400"
                  >
                    category
                  </span>
                  <div
                    role="group"
                    aria-labelledby="category-filter-label"
                    className="flex flex-wrap border border-gray-800"
                  >
                    {categoryOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={category === option.value}
                        onClick={() => setCategory(option.value)}
                        className={`border-r border-gray-800 last:border-r-0 ${toggleClass(
                          category === option.value,
                        )}`}
                      >
                        {option.label}{' '}
                        <span className="tabular-nums text-gray-400">
                          {option.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <label
                  htmlFor="documented-only"
                  className="flex cursor-pointer items-center gap-2 font-mono text-[11px] text-gray-400"
                >
                  <input
                    id="documented-only"
                    type="checkbox"
                    checked={documentedOnly}
                    onChange={event => setDocumentedOnly(event.target.checked)}
                    className="h-3.5 w-3.5 border-gray-600 bg-transparent accent-blue-500 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                  />
                  documented coverage only
                </label>

                <div className="flex items-center gap-2">
                  <span
                    id="view-mode-label"
                    className="font-mono text-[11px] uppercase tracking-wider text-gray-400"
                  >
                    view
                  </span>
                  <div
                    role="group"
                    aria-labelledby="view-mode-label"
                    className="flex border border-gray-800"
                  >
                    <button
                      type="button"
                      aria-pressed={view === 'table'}
                      onClick={() => setView('table')}
                      className={`border-r border-gray-800 ${toggleClass(
                        view === 'table',
                      )}`}
                    >
                      table
                    </button>
                    <button
                      type="button"
                      aria-pressed={view === 'rows'}
                      onClick={() => setView('rows')}
                      className={toggleClass(view === 'rows')}
                    >
                      rows
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Panel>

          <div className="mt-1.5 flex items-center gap-3 font-mono text-[11px] text-gray-400">
            <p aria-live="polite">
              showing{' '}
              <span className="tabular-nums text-gray-200">{rows.length}</span>{' '}
              of{' '}
              <span className="tabular-nums text-gray-200">{stats.total}</span>{' '}
              datasets
              {view === 'table' && (
                <>
                  {' · sorted by '}
                  <span className="text-gray-300">{sort.key}</span>{' '}
                  {sort.direction === 'asc' ? '▲' : '▼'}
                </>
              )}
            </p>
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-blue-400 underline hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              >
                clear filters
              </button>
            )}
          </div>
        </section>

        {/* Results */}
        <section aria-label="Datasets">
          <h2 className="sr-only">Datasets</h2>
          {rows.length === 0 ? (
            <div className="border border-gray-800 p-10 text-center">
              <p className="font-mono text-xs text-gray-400">
                no datasets match these filters
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-2 font-mono text-xs text-blue-400 underline hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              >
                clear filters
              </button>
            </div>
          ) : view === 'table' ? (
            <DatasetTable
              rows={rows}
              sort={sort}
              onSortChange={handleSortChange}
            />
          ) : (
            <DatasetRows rows={rows} />
          )}
        </section>

        {/* What the "documented" count means, stated plainly. */}
        <section aria-labelledby="coverage-notes" className="mt-8">
          <h2
            id="coverage-notes"
            className="font-mono text-[13px] uppercase tracking-wider text-gray-200"
          >
            What "documented" counts
          </h2>
          <p className="mt-1.5 max-w-3xl text-[13px] leading-relaxed text-gray-400">
            Each dataset is measured against the same {COVERAGE_FIELDS.length}{' '}
            fields institutional buyers diligence on. The count reports how many
            are published — it is not a quality score, so it cannot be moved by
            anything other than publishing the missing field.
          </p>
          <dl className="mt-3 grid gap-x-8 gap-y-0 text-xs sm:grid-cols-2 lg:grid-cols-3">
            {coverageFields.map(field => (
              <div
                key={String(field.key)}
                className="flex flex-wrap items-baseline gap-x-2 border-b border-dashed border-gray-800/70 py-1"
              >
                <dt className="font-mono text-[11px] text-gray-200">
                  {field.label}
                </dt>
                <dd className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-gray-400">
                  {field.documented}/{field.total}
                </dd>
                <dd className="w-full text-[11px] leading-tight text-gray-400">
                  {field.why}
                </dd>
              </div>
            ))}
          </dl>
          <ul className="mt-4 ml-4 list-disc space-y-0.5 text-[11px] leading-snug text-gray-400">
            <li>
              Entity counts are approximate and may grow as coverage expands.
            </li>
            <li>
              All datasets retain delisted names and corporate-action history to
              avoid survivorship bias.
            </li>
            <li>
              Economic datasets are absent from the published coverage table
              entirely, so their history and entity figures are genuinely
              unknown rather than withheld.
            </li>
            <li>
              Row counts are published only where an unfiltered pull returned
              the whole dataset. Ticker-partitioned datasets were sampled with a
              single-ticker filter, so their row counts are unknown rather than
              extrapolated.
            </li>
          </ul>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default DatasetsPage;
